import { useState } from "react";
import { useAppData } from "@/data/AppDataContext";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/StatusBadge";
import { Link } from "react-router-dom";
import { BooksNavRail } from "@/components/accounting/BooksNavRail";
import { BranchRegionFilter, EMPTY_BR, filterByBranchRegion, type BranchRegionState } from "@/components/BranchRegionFilter";
import { useFinancialPermissions } from "@/hooks/useFinancialPermissions";
import { ShieldAlert } from "lucide-react";
import {
  DollarSign, Search, TrendingUp, CreditCard, AlertTriangle, CheckCircle, PieChart, Receipt,
} from "lucide-react";

type FilterKey = "all" | "unpaid" | "overdue" | "payout-pending" | "paid";

const CURRENT_REP = "Mike Johnson";

export default function AccountingView() {
  const { jobs, credits } = useAppData();
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState<FilterKey>("all");
  const [brFilter, setBrFilter] = useState<BranchRegionState>(EMPTY_BR);
  const fin$ = useFinancialPermissions();

  if (!fin$.viewFinancialSummary && !fin$.viewContractValue) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-6">
        <div className="h-16 w-16 rounded-2xl bg-destructive/10 flex items-center justify-center mb-4">
          <ShieldAlert className="h-8 w-8 text-destructive" />
        </div>
        <h1 className="text-xl font-bold mb-2">Access Restricted</h1>
        <p className="text-sm text-muted-foreground max-w-md">
          Your current role does not have permission to view accounting and financial data.
          Contact your administrator if you believe this is an error.
        </p>
      </div>
    );
  }

  const baseJobs = fin$.scopedToOwn ? jobs.filter((j) => j.salesRep === CURRENT_REP) : jobs;
  const allJobs = filterByBranchRegion(baseJobs, brFilter);
  const jobsWithFinancials = allJobs.filter((j) => j.financials);

  const totalRevenue = jobsWithFinancials.reduce((s, j) => s + j.financials!.contractValue, 0);
  const totalMaterialCost = jobsWithFinancials.reduce((s, j) => s + j.financials!.estimatedMaterialCost, 0);
  const totalLaborCost = jobsWithFinancials.reduce((s, j) => s + j.financials!.estimatedLaborCost, 0);
  const totalSubPayout = jobsWithFinancials.reduce((s, j) => s + j.financials!.subcontractorPayout, 0);
  const totalMargin = jobsWithFinancials.reduce((s, j) => s + j.financials!.marginEstimate, 0);
  const marginPct = totalRevenue > 0 ? ((totalMargin / totalRevenue) * 100).toFixed(1) : "0";

  const unpaid = jobsWithFinancials.filter((j) => j.financials!.paymentStatus !== "Paid");
  const overdue = jobsWithFinancials.filter((j) => j.financials!.paymentStatus === "Overdue");
  const payoutPending = jobsWithFinancials.filter((j) => j.financials!.payoutStatus !== "Paid" && j.financials!.subcontractorPayout > 0);
  const paid = jobsWithFinancials.filter((j) => j.financials!.paymentStatus === "Paid");

  const views: { key: FilterKey; label: string; count: number; color: string }[] = [
    { key: "all", label: "All Jobs", count: jobsWithFinancials.length, color: "text-foreground" },
    { key: "unpaid", label: "Unpaid", count: unpaid.length, color: "text-warning" },
    { key: "overdue", label: "Overdue", count: overdue.length, color: "text-destructive" },
    { key: "payout-pending", label: "Payout Pending", count: payoutPending.length, color: "text-info" },
    { key: "paid", label: "Paid", count: paid.length, color: "text-success" },
  ];

  let filtered = jobsWithFinancials;
  if (activeFilter === "unpaid") filtered = unpaid;
  if (activeFilter === "overdue") filtered = overdue;
  if (activeFilter === "payout-pending") filtered = payoutPending;
  if (activeFilter === "paid") filtered = paid;

  if (search) {
    const s = search.toLowerCase();
    filtered = filtered.filter((j) =>
      j.id.toLowerCase().includes(s) || j.customerName.toLowerCase().includes(s) || j.branch.toLowerCase().includes(s)
    );
  }

  // Branch breakdown
  const branchMap = new Map<string, { revenue: number; margin: number; jobs: number }>();
  jobsWithFinancials.forEach((j) => {
    const b = branchMap.get(j.branch) || { revenue: 0, margin: 0, jobs: 0 };
    b.revenue += j.financials!.contractValue;
    b.margin += j.financials!.marginEstimate;
    b.jobs += 1;
    branchMap.set(j.branch, b);
  });

  return (
    <div className="space-y-5 max-w-7xl">
      <BooksNavRail />
      <div>
        <h1 className="text-lg font-bold tracking-tight flex items-center gap-2">
          <DollarSign className="h-5 w-5 text-primary" /> Accounting Visibility
        </h1>
        <p className="text-xs text-muted-foreground">Operational financial tracking · QuickBooks-ready</p>
      </div>

      {/* KPIs */}
      {(() => {
        const totalCredits = credits.reduce((s, c) => s + c.creditAmount, 0);
        const appliedCredits = credits.filter((c) => c.status === "Applied" || c.status === "Accepted").reduce((s, c) => s + c.creditAmount, 0);
        return (
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
            {[
              { label: "Total Revenue", value: `$${totalRevenue.toLocaleString()}`, icon: TrendingUp, color: "text-primary" },
              { label: "Material Cost", value: `$${totalMaterialCost.toLocaleString()}`, icon: CreditCard, color: "text-muted-foreground" },
              { label: "Labor + Sub Cost", value: `$${(totalLaborCost + totalSubPayout).toLocaleString()}`, icon: CreditCard, color: "text-muted-foreground" },
              { label: "Est. Margin", value: `$${totalMargin.toLocaleString()} (${marginPct}%)`, icon: PieChart, color: "text-success" },
              { label: "Overdue", value: overdue.length, icon: AlertTriangle, color: overdue.length > 0 ? "text-destructive" : "text-success" },
              { label: "Credits Issued", value: `$${totalCredits.toLocaleString()}`, icon: Receipt, color: totalCredits > 0 ? "text-warning" : "text-muted-foreground" },
            ].map((s) => (
              <Card key={s.label} className="border shadow-none">
                <CardContent className="p-3 text-center">
                  <s.icon className={`h-4 w-4 mx-auto mb-1 ${s.color}`} />
                  <p className="text-lg font-bold">{s.value}</p>
                  <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-medium">{s.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        );
      })()}

      {/* Branch Revenue Breakdown */}
      {branchMap.size > 1 && (
        <Card className="shadow-none">
          <CardContent className="p-4">
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em] mb-3">Revenue by Branch</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
              {Array.from(branchMap.entries()).map(([branch, data]) => (
                <div key={branch} className="rounded-lg border p-2.5 text-center">
                  <p className="text-[10px] text-muted-foreground font-medium mb-0.5">{branch}</p>
                  <p className="text-sm font-bold text-primary">${data.revenue.toLocaleString()}</p>
                  <p className="text-[9px] text-muted-foreground">{data.jobs} jobs · {data.revenue > 0 ? ((data.margin / data.revenue) * 100).toFixed(0) : 0}% margin</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        {views.map((v) => (
          <button
            key={v.key}
            onClick={() => setActiveFilter(v.key)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
              activeFilter === v.key
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-card hover:bg-muted border-border"
            }`}
          >
            {v.label} <span className={`ml-1 ${activeFilter !== v.key ? v.color : ""}`}>({v.count})</span>
          </button>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input placeholder="Search job, customer, branch..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 h-8 text-xs" />
        </div>
        <BranchRegionFilter jobs={jobs} value={brFilter} onChange={setBrFilter} />
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <p className="text-center text-xs text-muted-foreground py-8">No financial data matches your filters</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/40">
                    <th className="text-left py-2.5 px-4 font-medium text-muted-foreground">Job</th>
                    <th className="text-left py-2.5 px-4 font-medium text-muted-foreground">Customer</th>
                    <th className="text-left py-2.5 px-4 font-medium text-muted-foreground">Branch</th>
                    <th className="text-right py-2.5 px-4 font-medium text-muted-foreground">Contract</th>
                    <th className="text-right py-2.5 px-4 font-medium text-muted-foreground">Materials</th>
                    <th className="text-right py-2.5 px-4 font-medium text-muted-foreground">Labor</th>
                    <th className="text-right py-2.5 px-4 font-medium text-muted-foreground">Sub Payout</th>
                    <th className="text-right py-2.5 px-4 font-medium text-muted-foreground">Margin</th>
                    <th className="text-center py-2.5 px-4 font-medium text-muted-foreground">Payment</th>
                    <th className="text-center py-2.5 px-4 font-medium text-muted-foreground">Payout</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((j) => {
                    const f = j.financials!;
                    const mPct = f.contractValue > 0 ? ((f.marginEstimate / f.contractValue) * 100).toFixed(0) : "0";
                    return (
                      <tr key={j.id} className="border-b last:border-0 hover:bg-muted/30">
                        <td className="py-2 px-4">
                          <Link to={`/jobs/${j.id}`} className="text-primary font-medium hover:underline">{j.id}</Link>
                        </td>
                        <td className="py-2 px-4 font-medium">
                          <Link to={`/customers/${encodeURIComponent(j.customerName)}`} className="hover:text-primary transition-colors">{j.customerName}</Link>
                        </td>
                        <td className="py-2 px-4 text-muted-foreground">{j.branch}</td>
                        <td className="py-2 px-4 text-right font-semibold">${f.contractValue.toLocaleString()}</td>
                        <td className="py-2 px-4 text-right text-muted-foreground">${f.estimatedMaterialCost.toLocaleString()}</td>
                        <td className="py-2 px-4 text-right text-muted-foreground">${f.estimatedLaborCost.toLocaleString()}</td>
                        <td className="py-2 px-4 text-right text-muted-foreground">
                          {f.subcontractorPayout > 0 ? `$${f.subcontractorPayout.toLocaleString()}` : "—"}
                        </td>
                        <td className="py-2 px-4 text-right">
                          <span className={`font-semibold ${Number(mPct) > 30 ? "text-success" : Number(mPct) > 15 ? "text-foreground" : "text-warning"}`}>
                            ${f.marginEstimate.toLocaleString()} ({mPct}%)
                          </span>
                        </td>
                        <td className="py-2 px-4 text-center">
                          <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium border ${
                            f.paymentStatus === "Paid" ? "bg-success/10 text-success border-success/20" :
                            f.paymentStatus === "Overdue" ? "bg-destructive/10 text-destructive border-destructive/20" :
                            f.paymentStatus === "Partial Payment" ? "bg-warning/10 text-warning border-warning/20" :
                            "bg-muted text-muted-foreground border-border"
                          }`}>
                            {f.paymentStatus}
                          </span>
                        </td>
                        <td className="py-2 px-4 text-center">
                          {f.subcontractorPayout > 0 ? (
                            <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium border ${
                              f.payoutStatus === "Paid" ? "bg-success/10 text-success border-success/20" :
                              f.payoutStatus === "Approved" ? "bg-info/10 text-info border-info/20" :
                              "bg-muted text-muted-foreground border-border"
                            }`}>
                              {f.payoutStatus}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t bg-muted/20 font-semibold">
                    <td className="py-2.5 px-4" colSpan={3}>Totals ({filtered.length} jobs)</td>
                    <td className="py-2.5 px-4 text-right">${filtered.reduce((s, j) => s + j.financials!.contractValue, 0).toLocaleString()}</td>
                    <td className="py-2.5 px-4 text-right text-muted-foreground">${filtered.reduce((s, j) => s + j.financials!.estimatedMaterialCost, 0).toLocaleString()}</td>
                    <td className="py-2.5 px-4 text-right text-muted-foreground">${filtered.reduce((s, j) => s + j.financials!.estimatedLaborCost, 0).toLocaleString()}</td>
                    <td className="py-2.5 px-4 text-right text-muted-foreground">${filtered.reduce((s, j) => s + j.financials!.subcontractorPayout, 0).toLocaleString()}</td>
                    <td className="py-2.5 px-4 text-right text-success">${filtered.reduce((s, j) => s + j.financials!.marginEstimate, 0).toLocaleString()}</td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
